import React, { useRef } from 'react'

import { CheckIcon } from '@chakra-ui/icons'
import { Button } from '@chakra-ui/react'

import { CustomSummaryPartPrompt } from '@pkg/types/interview'

import { getAtsBrandColors } from '@/helpers/build-ats-url'
import { useInterview } from '@/react-query/interview/interview'
import { useTeam } from '@/react-query/team'
import { CopyIconSVG } from '@/views/assets/CopyIconSVG'
import { copyContentToClipboard } from '@/views/utils/copyDivToClipboard'

import { formatScorecardPartForCopy } from './formatScorecardPartTitle'

interface ViewATSScorecardButtonProps {
  summaryParts?: Array<{
    isSelected?: boolean | null
    name: string
    text: string
    order: number
    score?: number | null
    CustomSummaryPartPrompt?: CustomSummaryPartPrompt | null
  }>
}

export const ViewATSScorecardButton: React.FC<ViewATSScorecardButtonProps> = ({
  summaryParts,
}) => {
  const { data: team } = useTeam()
  const { data: interview } = useInterview()
  const [isRecentlyCopied, setIsRecentlyCopied] = React.useState<boolean>(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const candidateAtsProfileUrl = interview?.Candidate?.atsProfileUrl
  const atsIntegrationName = team?.atsIntegrationName || 'ATS'
  const brandColors = getAtsBrandColors(team?.atsIntegrationType)

  // Don't show button if no ATS connection or no candidate ATS profile URL.
  // We use the actual ATS profile URL (from the provider's web_url field)
  // rather than constructing URLs from Ashby IDs, which are internal
  // identifiers that don't resolve in the native ATS UI.
  if (!team?.isATSConnected || !candidateAtsProfileUrl) {
    return null
  }

  const atsUrl = candidateAtsProfileUrl
  const isAshby = team?.atsIntegrationType?.toLowerCase() === 'ashby'

  const handleCopyAndGo = async () => {
    if (!isAshby) {
      // Copy the scorecard content to clipboard using the same logic as CopyContentButton
      const textContent = (summaryParts || [])
        .filter((part) => part.isSelected)
        .sort((a, b) => a.order - b.order)
        .map((part) => formatScorecardPartForCopy(part))
        .join('\n\n')

      try {
        await copyContentToClipboard({
          'text/plain': textContent,
        })

        setIsRecentlyCopied(true)

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
          setIsRecentlyCopied(false)
        }, 1000)
      } catch (err) {
        console.error('Failed to copy text:', err)
      }
    }

    // Open the ATS URL in a new tab
    if (atsUrl) {
      window.open(atsUrl, '_blank')
    }
  }

  return (
    <Button
      onClick={handleCopyAndGo}
      leftIcon={
        isAshby ? undefined : isRecentlyCopied ? (
          <CheckIcon w={'1rem'} />
        ) : (
          <CopyIconSVG color="white" width={'1rem'} />
        )
      }
      bg={brandColors.bg}
      color="white"
      _hover={{ bg: brandColors.hover }}
      _active={{ bg: brandColors.active }}
      borderRadius="1.25rem"
      px={6}
    >
      {isAshby
        ? `Go to ${atsIntegrationName}`
        : `Copy and go to ${atsIntegrationName}`}
    </Button>
  )
}
